import { useState, useCallback, useRef } from 'react';
import type { Graph } from '@antv/x6';
import { startDataFlow as startDataFlowImpl, stopDataFlow as stopDataFlowImpl } from '../simulation/dataflow';
import { buildExecutionPlan, runExecution, cleanupSimulation } from '../simulation/execution';

export function useSimulation(
  graphRef: React.MutableRefObject<Graph | null>,
  setStatusText: (text: string) => void
) {
  const [dataFlowActive, setDataFlowActive] = useState(false);
  const [simulationRunning, setSimulationRunning] = useState(false);
  const executionRef = useRef<{ stop: () => void } | null>(null);

  const startDataFlow = useCallback(() => {
    if (!graphRef.current) return;
    startDataFlowImpl(graphRef.current);
    setDataFlowActive(true);
    setStatusText('数据流动画中...');
  }, [graphRef, setStatusText]);

  const stopDataFlow = useCallback(() => {
    if (!graphRef.current) return;
    stopDataFlowImpl(graphRef.current);
    setDataFlowActive(false);
    setStatusText('数据流已停止');
  }, [graphRef, setStatusText]);

  const startSimulation = useCallback(() => {
    if (!graphRef.current) return;
    const plan = buildExecutionPlan(graphRef.current);
    if (plan.steps.length === 0) {
      setStatusText('无可执行的过程');
      return;
    }
    setStatusText('执行模拟中...');
    setSimulationRunning(true);

    executionRef.current = runExecution(
      graphRef.current,
      plan,
      (stepIndex, step) => {
        setStatusText(`步骤 ${stepIndex + 1}/${plan.steps.length}: ${step.label || step.type}`);
      },
    );

    // 执行完成后自动停止
    const totalDuration = plan.steps.reduce((sum, s) => sum + s.duration, 0);
    setTimeout(() => {
      setSimulationRunning(false);
      setStatusText('执行模拟完成');
      executionRef.current = null;
    }, totalDuration + 200);
  }, [graphRef, setStatusText]);

  const stopSimulation = useCallback(() => {
    if (executionRef.current) {
      executionRef.current.stop();
      executionRef.current = null;
    }
    if (graphRef.current) {
      cleanupSimulation(graphRef.current);
    }
    setSimulationRunning(false);
    setStatusText('执行模拟已停止');
  }, [graphRef, setStatusText]);

  return {
    dataFlowActive,
    simulationRunning,
    startDataFlow,
    stopDataFlow,
    startSimulation,
    stopSimulation,
  };
}
